import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

export interface User {
  id: string;
  name: string;
  email: string;
  wordsToUse: number;
  wordsUsed: number;
  documentsToUse: number;
  documentsUsed: number;
  allowedLanguages: string[] | null;
  nrOfDrafts: number;
  nrOfDraftsUsed: number;
  status: number;
  createdAt: string;
  updatedAt: string;
  roleId: number;
  institutionId: string;
  indexId: string | null;
  facultyId: string;
  departmentId: string;
  provider: string | null;
  phoneNumber: string | null;
  lastLogin: string;
  thirdPartyEmailAct: boolean;
  marketingEmails: boolean;
  isDeleted: boolean;
  registrantId: string | null;
  language: string;
  Institution: {
    id: string;
    name: string;
    description: string;
    ltiSecret: string | null;
    createdAt: string;
    updatedAt: string;
    settings: any;
    clientId: string | null;
    InstitutionLicense: any[];
  };
}

export interface SharingContext {
  isShared: boolean;
  isViewOnly: boolean;
  userRole: 'owner' | 'recipient';
  shareType?: 'link_share' | 'email_share';
  accessType?: 'time_based' | 'unlimited' | 'one_time';
  accessPermission?: 'view' | 'edit';
  sharedBy?: {
    name: string;
    email: string;
  };
  shareMessage?: string | null;
  shareCreatedAt?: string;
  isPrecheckShared?: boolean;
}

export interface ShareDetails {
  share: {
    id: string;
    submissionId: string;
    shareType: 'email_share' | 'link_share';
    accessType: 'unlimited' | 'one_time' | 'time_based';
    accessPermission: 'view' | 'edit';
    shareCreatedAt: string;
    expiresAt?: string;
    status: string;
    sharedBy: {
      id: string;
      name: string;
      email: string;
    };
    submission: {
      id: string;
      title: string;
      filename: string;
      createdAt: string;
    };
  };
  othersWithAccess: {
    total: number;
    shares: Array<{
      shareType: 'email_share' | 'link_share';
      accessType: 'unlimited' | 'one_time' | 'time_based';
      accessPermission: 'view' | 'edit';
      sharedBy: string;
      shareCreatedAt: string;
      recipientInfo: string;
    }>;
  };
}

export interface SessionResponse {
  valid: boolean;
  user: User;
  submissionId: string;
  token: string;
  iframeUrl?: string;
  sharingContext: SharingContext;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly SHARING_CONTEXT_KEY = 'SHARING_CONTEXT';
  private readonly SESSION_ID_KEY = 'SESSION_ID';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router
  ) { }

  /**
   * Verifies a session with the backend
   * @param sessionId The session ID to verify
   * @returns Observable with session data
   */
  verifySession(sessionId: string): Observable<SessionResponse> {
    return this.http.post<SessionResponse>(
      `${environment.apiUrl}/external-sessions/verify`,
      { sessionId }
    ).pipe(
      map((response: SessionResponse) => {
        if (response.valid && response.token) {

          // Store authentication data
          localStorage.setItem('ACCESS_TOKEN', JSON.stringify(response.token));
          localStorage.setItem('role', JSON.stringify(response.user.roleId));
          localStorage.setItem('user', JSON.stringify(response.user));
          localStorage.setItem('websiteLanguage', response.user.language || 'en');

          // Store sharing context securely with sessionId
          this.storeSharingContext(response.sharingContext, sessionId);
        }
        return response;
      })
    );
  }

  /**
   * Stores sharing context with a hash to prevent manipulation
   * @param sharingContext The sharing context from session response
   * @param sessionId The original session ID for hash generation
   */
  private storeSharingContext(sharingContext: SharingContext, sessionId: string): void {
    localStorage.setItem(this.SHARING_CONTEXT_KEY, JSON.stringify(sharingContext));
    localStorage.setItem(this.SESSION_ID_KEY, sessionId);
  }

  /**
   * Retrieves and validates the sharing context - IMMEDIATE REVOCATION if tampered
   * @returns The sharing context if valid, null if tampered (access revoked)
   */
  getSharingContext(): SharingContext | null {
    try {
      const storedContext = localStorage.getItem(this.SHARING_CONTEXT_KEY);

      if (!storedContext) {
        // Missing data - might be initial load or cleared session
        return null;
      }

      const sharingContext: SharingContext = JSON.parse(storedContext);
      return sharingContext;
    } catch (error) {
      console.error('Error retrieving sharing context:', error);
      return null;
    }
  }

  /**
   * Checks if the current session is a shared session
   */
  isSharedSession(): boolean {
    const context = this.getSharingContext();
    return context?.isShared || false;
  }

  /**
   * Checks if the current session is view-only
   */
  isViewOnlySession(): boolean {
    const context = this.getSharingContext();
    return context?.isViewOnly || false;
  }

  /**
   * Gets the current user's role in the sharing context
   */
  getUserRole(): 'owner' | 'recipient' | null {
    const context = this.getSharingContext();
    return context?.userRole || null;
  }

  /**
   * Clears sharing context from storage
   */
  clearSharingContext(): void {
    localStorage.removeItem(this.SHARING_CONTEXT_KEY);
    localStorage.removeItem(this.SESSION_ID_KEY);
  }

  /**
   * Clears ALL session data - used for security breaches
   */
  private clearAllSessionData(): void {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('websiteLanguage');
    this.clearSharingContext();

    // Clear any other potential session-related data
    localStorage.clear();
  }

  /**
   * Redirects to the appropriate report page based on session data
   * @param sessionData The session response data
   * @param queryParams Optional query parameters to preserve in the URL
   */
  navigateToReport(sessionData: SessionResponse, queryParams?: any): void {
    const { submissionId } = sessionData;

    if (!submissionId) {
      this.router.navigate(['/not-found']);
      return;
    }

      // For IO route
      this.router.navigate([
        '/submission',
        submissionId,
        'report',
        'v2'
      ], { queryParams: queryParams || {} });
  }

  /**
   * Clears all session data
   */
  clearSession(): void {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('websiteLanguage');
    this.clearSharingContext();
  }

  /**
   * Enhanced security check for sensitive operations
   * @returns boolean indicating if the current session can perform the operation
   */
  canPerformSensitiveOperation(): boolean {
    // Get the context
    const context = this.getSharingContext();
    if (!context) {
      return false;
    }

    // Additional security checks
    if (context.isShared && context.isViewOnly) {
      console.warn('Operation denied: view-only access');
      return false;
    }

    if (context.userRole === 'recipient' && context.isShared) {
      console.warn('Operation denied: recipient role in shared session');
      return false;
    }

    return true;
  }

  /**
   * Re-validates session with backend (for critical operations)
   * @returns Observable with fresh session data
   */
  revalidateSession(): Observable<boolean> {
    const storedSessionId = localStorage.getItem(this.SESSION_ID_KEY);
    if (!storedSessionId) {
      return new Observable(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    return this.verifySession(storedSessionId).pipe(
      map(response => response.valid)
    );
  }

  /**
   * Get details about a specific share
   * @param sessionId The session ID of the share to fetch
   * @returns Observable with share details including others with access
   */
  getShareDetails(sessionId: string): Observable<ShareDetails> {
    return this.http.get<ShareDetails>(`${environment.apiUrl}/get/shares/${sessionId}`);
  }
}
